/**
 * RoleChip — colour-coded role badge, shared by the admin user list and the
 * admin user detail page so a role always looks the same in both places.
 *
 * The nine keys below are exactly the roles the DB allows
 * (users_role_check in migrations/baseline_schema.sql). Anything else falls
 * back to neutral grey rather than rendering an unstyled chip.
 */

import React from 'react';
import { Chip } from '@mui/material';
import { status as statusTokens, text } from '../../theme/colors';

// Distinct hue per role so a long list is scannable at a glance. Each entry is
// a foreground/background pair; backgrounds are ~8% tints of the foreground.
export const ROLE_TONES = {
  admin: { fg: '#7c3aed', bg: 'rgba(124, 58, 237, 0.10)' }, // Violet
  moderator: { fg: '#0e7490', bg: 'rgba(14, 116, 144, 0.10)' }, // Cyan 700
  client: { fg: '#1d4ed8', bg: 'rgba(29, 78, 216, 0.10)' }, // Blue 700
  contractor: { fg: '#c2410c', bg: 'rgba(194, 65, 12, 0.10)' }, // Terracotta (brand secondary)
  verifier: { fg: '#0f766e', bg: 'rgba(15, 118, 110, 0.10)' }, // Teal 700 (brand)
  quality_inspector: { fg: '#b45309', bg: 'rgba(180, 83, 9, 0.10)' }, // Amber 700
  project_manager: { fg: '#be185d', bg: 'rgba(190, 24, 93, 0.10)' }, // Pink 700
  merchant: { fg: '#15803d', bg: 'rgba(21, 128, 61, 0.10)' }, // Green 700
  realtor: { fg: '#6d28d9', bg: 'rgba(109, 40, 217, 0.10)' }, // Purple 700
  conveyancer: { fg: '#9a3412', bg: 'rgba(154, 52, 18, 0.10)' }, // Orange 800
};

const FALLBACK = { fg: statusTokens.neutral, bg: statusTokens.neutralBg };

export const roleTone = (role) => ROLE_TONES[role] || FALLBACK;

const RoleChip = ({ role, size = 'small', sx }) => {
  const tone = roleTone(role);
  return (
    <Chip
      size={size}
      label={(role || 'unknown').replace(/_/g, ' ')}
      sx={{
        textTransform: 'capitalize',
        fontWeight: 600,
        fontSize: 12,
        color: tone.fg,
        bgcolor: tone.bg,
        border: `1px solid ${tone.fg}33`,
        ...(role ? {} : { color: text.tertiary }),
        ...sx,
      }}
    />
  );
};

export default RoleChip;
